import { Button } from '@/components/ui/Button';
import { resourceCoverUrl } from '../resource-cover';
import { RESOURCE_ACTION_LABEL, resolveResourceAction } from '../resource-action';
import type { ResourceItemDto } from '@/lib/types/resources.types';

export interface ResourceListItemProps {
  item: ResourceItemDto;
  /** Called when the title or the open action is activated. Caller resolves URL/download. */
  onOpen: () => void;
}

/**
 * One row of the folder page's resource list (FR-158): a 2:1 cover thumbnail,
 * the title, a clamped description and the open action.
 *
 * The thumbnail is about a third of the row beside the text from `sm` up and
 * sits above the text on mobile. The title opens the resource, as the action
 * does; the action's label comes from `resolveResourceAction`, so it always
 * agrees with what the open handler does. Falls back to the placeholder photo
 * when the item has no cover.
 */
export function ResourceListItem({ item, onOpen }: ResourceListItemProps) {
  const actionLabel = RESOURCE_ACTION_LABEL[resolveResourceAction(item)];
  const coverUrl = resourceCoverUrl(item.coverImageUrl);

  return (
    <article className="rounded-card shadow-card hover:shadow-overlay flex flex-col gap-4 bg-white p-4 transition-shadow sm:flex-row sm:items-start sm:gap-6">
      <div className="bg-bg aspect-[2/1] w-full shrink-0 overflow-hidden rounded-xl sm:w-1/3">
        {coverUrl && <img src={coverUrl} alt="" className="h-full w-full object-cover" />}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <h3 className="text-h6 text-navy">
          <Button
            variant="link"
            onClick={onOpen}
            className="text-navy! hover:text-green! text-left hover:underline"
          >
            {item.title}
          </Button>
        </h3>
        <p className="text-body line-clamp-3 text-sm">{item.description}</p>
        <div className="mt-1">
          <Button variant="link" onClick={onOpen} className="text-sm hover:underline">
            {actionLabel}
          </Button>
        </div>
      </div>
    </article>
  );
}
